/**
 * Admin Analytics Dashboard (F9) — WEB, iteration-1 for Manak review.
 *
 * Squad WEB-DASH, SS backlog parallel sprint 2026-07-06.
 * Route: /admin/analytics (admin/owner/manager gated — mirrors other admin routes).
 *
 * Sections
 *  1. Selector row       — scope (Executive/Team/Employee) + date-window picker.
 *  2. Assignment vs Action — per selection: leads assigned vs actions taken.
 *  3. Hot-lead feed       — live HOT/WARM/COLD lead feed + temperature filter chip.
 *  4. Hot-lead history / KPI tiles — hot-lead count over time (line) + tiles.
 *
 * Data: reuses existing backend aggregates via ../../api/adminAnalytics.ts.
 * Recharts (^2.14) for viz — matches AnalyticsPage.tsx chart style.
 *
 * GAP NOTES (surfaced inline in the UI, iteration-1):
 *  - The role-kpi endpoints scope to the AUTHENTICATED user's own tree
 *    (MY/TEAM/DEPT/ORG), NOT an arbitrary rep/team id. So the "Employee" scope
 *    resolves via the scorecard `?userId=` route (which DOES accept a target),
 *    while "Team"/"Executive" scope reflects the logged-in user's subtree.
 *    A true admin "inspect any rep/team" cut for the Assignment-vs-Action bars
 *    needs a new endpoint: `GET /analytics/sales/rep-activity?userId=|teamId=`.
 *  - Hot-lead-count OVER TIME (a time-series) has no dedicated endpoint. We
 *    approximate the trend by bucketing the current HOT feed's createdAt by day.
 *    A precise historical series needs `GET /analytics/leads/tier-history`.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import {
  Loader2, Flame, Thermometer, Snowflake, Users, TrendingUp, IndianRupee,
  Target, AlertTriangle, RefreshCw, Info,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { formatINR } from '@/utils/formatters';
import {
  fetchAssignmentVsAction, fetchLeadFeed, fetchOrgStats, fetchRepOptions, fetchTeamOptions,
  fetchScorecard, type Scope, type DateWindow, type KpiPayload, type TriageRow,
  type RepOption, type TeamOption, type OrgStats, type ScorecardDetail,
} from '../../api/adminAnalytics';

// ─── Palette (SS forest/saffron) ────────────────────────────────────────────
const FOREST = '#1F4B3A';
const SAFFRON = '#E07A16';
const TIER_COLORS: Record<string, string> = { HOT: '#DC2626', WARM: '#E07A16', COLD: '#2563EB' };
const CHART_PALETTE = [FOREST, SAFFRON, '#2563EB', '#7C3AED', '#0D9488', '#DB2777', '#65A30D', '#9333EA'];

type ScopeMode = 'executive' | 'team' | 'employee';
type WindowPreset = 'today' | '7d' | '30d' | 'custom';

// ─── small helpers ───────────────────────────────────────────────────────────
// SBK-WEB-R7: format a Date to yyyy-mm-dd in LOCAL time (not UTC). `toISOString()`
// is UTC, so for IST (UTC+5:30) during 00:00–05:30 "Today" would resolve to
// yesterday's calendar date. Build the string from the local Y/M/D parts instead.
function localIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localIso(d);
}
function todayIso(): string {
  return localIso(new Date());
}
function resolveWindow(preset: WindowPreset, custom: DateWindow): DateWindow {
  if (preset === 'today') return { from: todayIso(), to: todayIso() };
  if (preset === '7d') return { from: isoDaysAgo(7), to: todayIso() };
  if (preset === '30d') return { from: isoDaysAgo(30), to: todayIso() };
  // SBK-WEB-R7: swap an inverted custom range (from > to) so a mistyped
  // range doesn't silently produce an empty feed.
  if (custom.from && custom.to && custom.from > custom.to) {
    return { from: custom.to, to: custom.from };
  }
  return custom;
}
/** Executive scope (rep-tree) uses role-kpi scope=MY; Team uses TEAM; else ORG. */
function scopeForMode(mode: ScopeMode): Scope {
  if (mode === 'executive') return 'MY';
  if (mode === 'team') return 'TEAM';
  return 'ORG';
}

// ─── shells ──────────────────────────────────────────────────────────────────
function Card({ title, subtitle, right, children }: {
  title?: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--card)] p-4 shadow-sm">
      {(title || right) && (
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            {title && <h3 className="text-sm font-semibold text-[var(--ink-0)]">{title}</h3>}
            {subtitle && <p className="text-xs text-[var(--ink-3)]">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function GapNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-md bg-[var(--warn-bg)] px-2 py-1.5 text-[11px] text-[var(--ink-2)]">
      <Info size={13} className="mt-px shrink-0 text-[var(--warn)]" />
      <span>{children}</span>
    </div>
  );
}

function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-10 text-[var(--ink-3)]">
      <Loader2 size={20} className="animate-spin text-[var(--saffron-600)]" />
      {label && <span className="ml-2 text-sm">{label}</span>}
    </div>
  );
}

// ─── KPI tile ─────────────────────────────────────────────────────────────────
function Tile({ icon: Icon, label, value, tone }: {
  icon: LucideIcon;
  label: string; value: string; tone: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--card)] p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${tone}18` }}>
          <Icon size={18} className="" />
        </span>
        <span className="text-xs font-medium text-[var(--ink-3)]">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold text-[var(--ink-0)]">{value}</p>
    </div>
  );
}

export function AnalyticsDashboardPage() {
  // SBK-WEB-R2: authorization lives at the ROUTE level (RequireRoles in App.tsx),
  // which unmounts the page for non-authorized roles and renders nothing while
  // the auth store is still hydrating. `ready` is a defense-in-depth guard so
  // that even if this page is ever rendered without the wrapper, NO fetch fires
  // until `user` is known.
  const { user } = useAuthStore();
  const ready = !!user;

  // ── Selector state ──────────────────────────────────────────────────────
  const [scopeMode, setScopeMode] = useState<ScopeMode>('team');
  const [employeeId, setEmployeeId] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [windowPreset, setWindowPreset] = useState<WindowPreset>('30d');
  const [customWindow, setCustomWindow] = useState<DateWindow>({ from: isoDaysAgo(30), to: todayIso() });

  const [repOptions, setRepOptions] = useState<RepOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<TeamOption[]>([]);

  const activeWindow = useMemo(() => resolveWindow(windowPreset, customWindow), [windowPreset, customWindow]);
  const scope = scopeForMode(scopeMode);

  // SBK-WEB-R4: clear a stale teamId when leaving Team mode, so the team gap-note
  // (which fires on `teamId` being set) doesn't spuriously appear in Executive/
  // Employee scope.
  function changeScopeMode(next: ScopeMode) {
    if (next !== 'team') setTeamId('');
    setScopeMode(next);
  }

  // ── Section data ────────────────────────────────────────────────────────
  const [ava, setAva] = useState<Awaited<ReturnType<typeof fetchAssignmentVsAction>> | null>(null);
  const [avaLoading, setAvaLoading] = useState(true);
  const [scorecard, setScorecard] = useState<ScorecardDetail | null>(null);
  // SBK-WEB-R3: explicit scorecard-load state so a 403 (manager → out-of-chain
  // employee) surfaces a message instead of an infinite "Loading scorecard…".
  const [scorecardState, setScorecardState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [feed, setFeed] = useState<TriageRow[]>([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedLoading, setFeedLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<'any' | 'HOT' | 'WARM' | 'COLD'>('HOT');
  const [orgStats, setOrgStats] = useState<OrgStats | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Load selector option lists once (only after the store has hydrated).
  useEffect(() => {
    if (!ready) return;
    fetchRepOptions().then(setRepOptions);
    fetchTeamOptions().then(setTeamOptions);
  }, [ready]);

  // Assignment-vs-Action + scorecard (Employee scope) — reacts to scope/tick only.
  // SBK-WEB-R5: these role-KPI aggregates are today/MTD/all-time fixed windows;
  // the date-window selector does NOT apply here (only the lead feed + trend honor it).
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setAvaLoading(true);
    fetchAssignmentVsAction(scope)
      .then((r) => { if (!cancelled) setAva(r); })
      .catch(() => { if (!cancelled) setAva(null); })
      .finally(() => { if (!cancelled) setAvaLoading(false); });
    if (scopeMode === 'employee' && employeeId) {
      // SBK-WEB-R3: reset the previous employee's card BEFORE the re-fetch so a
      // stale card doesn't linger, and mark an explicit error state on a 403 so
      // an out-of-chain employee shows a message instead of a forever spinner.
      setScorecard(null);
      setScorecardState('loading');
      fetchScorecard(employeeId)
        .then((s) => { if (!cancelled) { setScorecard(s); setScorecardState(s ? 'idle' : 'error'); } })
        .catch(() => { if (!cancelled) { setScorecard(null); setScorecardState('error'); } });
    } else {
      setScorecard(null);
      setScorecardState('idle');
    }
    return () => { cancelled = true; };
  }, [ready, scope, scopeMode, employeeId, refreshTick]);

  // Hot-lead feed — react to tier/window/tick.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setFeedLoading(true);
    fetchLeadFeed({ tier: tierFilter, window: activeWindow, tab: 'assigned', limit: 100, sort: 'score_desc' })
      .then((r) => { if (!cancelled) { setFeed(r.data); setFeedTotal(r.total); } })
      .catch(() => { if (!cancelled) { setFeed([]); setFeedTotal(0); } })
      .finally(() => { if (!cancelled) setFeedLoading(false); });
    return () => { cancelled = true; };
  }, [ready, tierFilter, activeWindow.from, activeWindow.to, refreshTick]);

  // Org stats (KPI tiles).
  useEffect(() => {
    if (!ready) return;
    fetchOrgStats().then(setOrgStats).catch(() => setOrgStats(null));
  }, [ready, refreshTick]);

  // ── derived: Assignment vs Action chart ────────────────────────────────
  // "Assigned" = leads counted by source-mix total (proxy for leads in scope).
  // "Actions"  = activities today + overdue-followups + proposals-pending-ish.
  const assignedTotal = ava?.leadSourceMix.value ?? 0;
  const actionsTotal = ava?.activitiesToday.value ?? 0;
  const overdueTotal = ava?.overdueFollowups.value ?? 0;

  // Per-rep assignment-vs-action: merge rep-leaderboard (won value) + quota (collected).
  const perRepRows = useMemo(() => {
    if (!ava) return [];
    const byName = new Map<string, { name: string; won: number; collected: number }>();
    for (const b of ava.repLeaderboard.breakdown ?? []) {
      const r = byName.get(b.label) ?? { name: b.label, won: 0, collected: 0 };
      r.won = b.value; byName.set(b.label, r);
    }
    for (const b of ava.quotaAttainment.breakdown ?? []) {
      const r = byName.get(b.label) ?? { name: b.label, won: 0, collected: 0 };
      r.collected = b.value; byName.set(b.label, r);
    }
    return Array.from(byName.values()).sort((a, b) => (b.won + b.collected) - (a.won + a.collected)).slice(0, 12);
  }, [ava]);

  const stageData = useMemo(
    () => (ava?.stageConversion.breakdown ?? []).map((b) => ({ stage: b.label, value: b.value })),
    [ava],
  );
  const sourceData = useMemo(
    () => (ava?.leadSourceMix.breakdown ?? []).map((b) => ({ name: b.label, value: b.value })),
    [ava],
  );

  // Hot-lead-count over time — bucket current feed by day (proxy; see gap note).
  const tierTrend = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const r of feed) {
      const day = (r.enquiryDate ?? r.createdAt?.slice(0, 10)) || 'unknown';
      byDay.set(day, (byDay.get(day) ?? 0) + 1);
    }
    return Array.from(byDay.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([day, count]) => ({ day: day.slice(5), count }));
  }, [feed]);

  const dealWonPct = orgStats && orgStats.deals.total > 0
    ? Math.round((orgStats.deals.won / orgStats.deals.total) * 100) : 0;

  return (
    <div className="mx-auto max-w-[1400px] pb-10">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--ink-0)]">Analytics Dashboard</h1>
          <p className="text-sm text-[var(--ink-3)]">
            Assignment vs action, hot-lead feed, and KPI tiles · iteration-1
          </p>
        </div>
        <button
          onClick={() => setRefreshTick((t) => t + 1)}
          className="flex items-center gap-1.5 rounded-lg border border-[var(--line)] px-3 py-1.5 text-sm text-[var(--ink-1)] hover:bg-[var(--bg-1)]"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Section 1: Selector row ────────────────────────────────────── */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          {/* Scope */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">Scope</label>
            <div className="inline-flex rounded-lg border border-[var(--line)] p-0.5">
              {([['executive', 'Executive'], ['team', 'Team'], ['employee', 'Employee']] as const).map(([k, lbl]) => (
                <button
                  key={k}
                  onClick={() => changeScopeMode(k)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    scopeMode === k ? 'bg-[var(--forest)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--bg-1)]'
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Employee picker */}
          {scopeMode === 'employee' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">Employee</label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="rounded-lg border border-[var(--line)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--ink-0)]"
              >
                <option value="">Select employee…</option>
                {repOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}

          {/* Team picker */}
          {scopeMode === 'team' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">Team</label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="rounded-lg border border-[var(--line)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--ink-0)]"
              >
                <option value="">My team (self + reports)</option>
                {teamOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Date window */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">Window</label>
            {/* SBK-WEB-R5: the window only filters the lead feed + trend below;
                KPI tiles + Assignment-vs-Action are fixed today/MTD/all-time. */}
            <div className="inline-flex rounded-lg border border-[var(--line)] p-0.5">
              {(['today', '7d', '30d', 'custom'] as const).map((w) => (
                <button
                  key={w}
                  onClick={() => setWindowPreset(w)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    windowPreset === w ? 'bg-[var(--saffron)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--bg-1)]'
                  }`}
                >
                  {w === 'today' ? 'Today' : w === 'custom' ? 'Custom' : w}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-[var(--ink-4)]">Applies to the lead feed only</p>
          </div>

          {/* Custom range */}
          {windowPreset === 'custom' && (
            <div className="flex items-end gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">From</label>
                <input
                  type="date"
                  value={customWindow.from ?? ''}
                  onChange={(e) => setCustomWindow((w) => ({ ...w, from: e.target.value || null }))}
                  className="rounded-lg border border-[var(--line)] bg-[var(--card)] px-2 py-1.5 text-sm text-[var(--ink-0)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--ink-3)]">To</label>
                <input
                  type="date"
                  value={customWindow.to ?? ''}
                  onChange={(e) => setCustomWindow((w) => ({ ...w, to: e.target.value || null }))}
                  className="rounded-lg border border-[var(--line)] bg-[var(--card)] px-2 py-1.5 text-sm text-[var(--ink-0)]"
                />
              </div>
            </div>
          )}
        </div>
        {scopeMode !== 'employee' && (teamId) && (
          <GapNote>
            Team drill-down by a specific team id isn't wired to the KPI aggregates yet — showing
            <b> your own team subtree</b>. Needs a <code>?teamId=</code> variant on the sales KPI endpoints.
          </GapNote>
        )}
      </Card>

      {/* ── Section 4 (tiles up top for scan-ability) ──────────────────── */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Tile icon={Users} label="Total Leads (feed)" value={String(feedTotal)} tone={FOREST} />
        <Tile icon={Flame} label="Deals Won" value={String(orgStats?.deals.won ?? '—')} tone="#DC2626" />
        <Tile icon={TrendingUp} label="Win Rate" value={`${dealWonPct}%`} tone={SAFFRON} />
        <Tile icon={IndianRupee} label="Collected"
          value={orgStats ? formatINR(orgStats.revenue.totalCollected) : '—'} tone="#0D9488" />
        <Tile icon={AlertTriangle} label="Overdue Follow-ups" value={String(overdueTotal)} tone="#9333EA" />
      </div>

      {/* ── Section 2: Assignment vs Action ────────────────────────────── */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Assignment vs Action"
            subtitle={
              // SBK-WEB-R4: `scopeForMode('employee')` maps to ORG, so this card
              // shows org-wide totals in Employee mode. Label it honestly rather
              // than implying a per-employee cut (which needs the rep-activity endpoint).
              scopeMode === 'employee'
                ? 'Org-wide — per-employee cut pending rep-activity endpoint'
                : `Scope: ${scopeMode === 'team' ? 'your team subtree' : 'org-wide'} · leads in scope vs actions taken`
            }
          >
            {avaLoading ? <Spinner label="Loading activity…" /> : (
              <>
                <ResponsiveContainer width="100%" height={70}>
                  <BarChart
                    layout="vertical"
                    data={[{ name: 'Totals', Assigned: assignedTotal, Actions: actionsTotal, Overdue: overdueTotal }]}
                    margin={{ top: 4, right: 20, left: 10, bottom: 4 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Assigned" fill={FOREST} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="Actions" fill={SAFFRON} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="Overdue" fill="#DC2626" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>

                {/* per-rep breakdown */}
                <h4 className="mb-1 mt-3 text-xs font-semibold text-[var(--ink-2)]">Per-rep (won vs collected, ₹)</h4>
                {perRepRows.length === 0 ? (
                  <p className="py-6 text-center text-sm text-[var(--ink-3)]">No rep breakdown in scope</p>
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(140, perRepRows.length * 30)}>
                    <BarChart layout="vertical" data={perRepRows} margin={{ top: 4, right: 20, left: 70, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={64} />
                      <Tooltip formatter={(v: number) => formatINR(v)} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="won" name="Won" stackId="a" fill={FOREST} />
                      <Bar dataKey="collected" name="Collected" stackId="a" fill={SAFFRON} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <GapNote>
                  "Assigned" is a lead-count proxy (source-mix total) and "Actions" = activities logged today —
                  the role-KPI endpoints scope to <b>your own tree</b>, not an arbitrary rep/team. A precise
                  per-rep <i>assigned-vs-actions</i> cut needs <code>GET /analytics/sales/rep-activity?userId=</code>.
                </GapNote>
              </>
            )}
          </Card>
        </div>

        {/* stage + source pies */}
        <div className="flex flex-col gap-4">
          <Card title="Open deals by stage">
            {avaLoading ? <Spinner /> : stageData.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--ink-3)]">No stage data</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={stageData} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={44} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" name="Open" fill={FOREST} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
          <Card title="Lead source mix">
            {avaLoading ? <Spinner /> : sourceData.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--ink-3)]">No source data</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={(e: any) => e.name}>
                    {sourceData.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>
      </div>

      {/* Employee scorecard (only in Employee scope) */}
      {scopeMode === 'employee' && (
        <div className="mt-4">
          <Card title="Employee scorecard" subtitle={scorecard ? scorecard.name : 'Select an employee'}>
            {!employeeId ? (
              <p className="py-6 text-center text-sm text-[var(--ink-3)]">Pick an employee in the selector above</p>
            ) : scorecardState === 'error' ? (
              <p className="py-6 text-center text-sm text-[var(--ink-3)]">
                Not authorized to view this employee's scorecard (or no data). The scorecard endpoint
                only exposes reps within your own reporting chain.
              </p>
            ) : scorecardState === 'loading' || !scorecard ? (
              <Spinner label="Loading scorecard…" />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Tile icon={Target} label="Score" value={scorecard.score != null ? String(scorecard.score) : '—'} tone={FOREST} />
                {scorecard.tiles.slice(0, 3).map((t, i) => (
                  <Tile key={i} icon={TrendingUp} label={t.label} value={`${t.value}${t.unit ? ' ' + t.unit : ''}`} tone={SAFFRON} />
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── Section 3: Hot-lead feed + temperature filter ──────────────── */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Lead feed"
            subtitle={`${feedTotal} leads · ${tierFilter === 'any' ? 'all tiers' : tierFilter} · ${windowPreset}`}
            right={
              <div className="inline-flex rounded-lg border border-[var(--line)] p-0.5">
                {([
                  ['HOT', 'Hot', Flame], ['WARM', 'Warm', Thermometer], ['COLD', 'Cold', Snowflake], ['any', 'All', Users],
                ] as const).map(([k, lbl, Ic]) => (
                  <button
                    key={k}
                    onClick={() => setTierFilter(k)}
                    className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                      tierFilter === k
                        ? 'text-white'
                        : 'text-[var(--ink-2)] hover:bg-[var(--bg-1)]'
                    }`}
                    style={tierFilter === k ? { backgroundColor: k === 'any' ? FOREST : TIER_COLORS[k] } : undefined}
                  >
                    <Ic size={12} /> {lbl}
                  </button>
                ))}
              </div>
            }
          >
            {feedLoading ? <Spinner label="Loading feed…" /> : feed.length === 0 ? (
              <p className="py-10 text-center text-sm text-[var(--ink-3)]">No leads match this filter</p>
            ) : (
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[var(--card)] text-left text-xs text-[var(--ink-3)]">
                    <tr className="border-b border-[var(--line)]">
                      <th className="py-2 pr-2 font-medium">Lead</th>
                      <th className="py-2 pr-2 font-medium">Tier</th>
                      <th className="py-2 pr-2 font-medium">Score</th>
                      <th className="py-2 pr-2 font-medium">Assigned</th>
                      <th className="py-2 pr-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feed.map((r) => (
                      <tr key={r.id} className="border-b border-[var(--line)] hover:bg-[var(--bg-1)]">
                        <td className="py-2 pr-2">
                          <div className="font-medium text-[var(--ink-0)]">{r.name || 'Unnamed'}</div>
                          <div className="text-xs text-[var(--ink-3)]">{r.phone || '—'}{r.location ? ` · ${r.location}` : ''}</div>
                        </td>
                        <td className="py-2 pr-2">
                          {r.tier ? (
                            <span
                              className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                              style={{ backgroundColor: TIER_COLORS[r.tier] ?? '#6B7280' }}
                            >
                              {r.tier}
                            </span>
                          ) : <span className="text-xs text-[var(--ink-4)]">—</span>}
                        </td>
                        <td className="py-2 pr-2 font-mono text-xs text-[var(--ink-1)]">{r.score ?? '—'}</td>
                        <td className="py-2 pr-2 text-xs text-[var(--ink-2)]">{r.assignedToName || 'Unassigned'}</td>
                        <td className="py-2 pr-2 text-xs text-[var(--ink-3)]">{r.enquiryDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        {/* ── Section 4: Hot-lead history (line) ──────────────────────── */}
        <div>
          <Card title="Lead count over time" subtitle={`${tierFilter === 'any' ? 'All tiers' : tierFilter} · by day`}>
            {feedLoading ? <Spinner /> : tierTrend.length === 0 ? (
              <p className="py-10 text-center text-sm text-[var(--ink-3)]">No dated leads in window</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={tierTrend} margin={{ top: 8, right: 12, left: -18, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone" dataKey="count" name="Leads"
                    stroke={tierFilter === 'any' ? FOREST : (TIER_COLORS[tierFilter] ?? SAFFRON)}
                    strokeWidth={2} dot={{ r: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
            <GapNote>
              Trend is bucketed from the <b>current feed</b> (not a stored time-series). A precise historical
              hot-lead curve needs <code>GET /analytics/leads/tier-history</code>.
            </GapNote>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboardPage;
